import React from 'react';
import Icon from '../lib/Icon';
import AssessmentStatusPill from './AssessmentStatusPill';
import { ASSESSMENT_STATUS } from '../lib/status';

function SeqNode({ status, size }) {
  const m = ASSESSMENT_STATUS[status];
  const ring = size === 'lg' ? 46 : 34;
  return (
    <div style={{
      width: ring, height: ring, flex: 'none', borderRadius: 999,
      border: status === 'complete' ? 'none' : `2px solid ${m.ring}`,
      background: status === 'complete' ? m.fill : status === 'active' ? m.soft : '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: status === 'active' ? `0 0 0 4px ${m.soft}` : 'none', boxSizing: 'border-box',
    }}>
      {status === 'complete' && <Icon name="check" size={size === 'lg' ? 22 : 16} stroke={2.6} style={{ color: 'var(--ink)' }} />}
      {status === 'active' && <span style={{ width: ring * 0.32, height: ring * 0.32, borderRadius: 999, background: m.ring }} />}
      {status === 'scheduled' && <span style={{ width: ring * 0.26, height: ring * 0.26, borderRadius: 999, background: m.ring }} />}
      {status === 'locked' && <Icon name="lock" size={size === 'lg' ? 16 : 12} style={{ color: 'var(--ink-faint)' }} />}
    </div>
  );
}

function HalfLine({ on, hidden }) {
  return <div style={{ flex: 1, height: 3, borderRadius: 2, background: hidden ? 'transparent' : on ? 'var(--science-400)' : 'var(--neutral-100)' }} />;
}

/**
 * N assessment nodes on a half-connector timeline. Each column:
 * [left-half line][node][right-half line] so any count stays evenly spaced.
 * size 'sm' (cards: name labels only) | 'lg' (workspace: + window + status pill)
 */
export default function AssessmentTimeline({ assessments, size = 'sm', onSelect }) {
  return (
    <div style={{ display: 'flex', width: '100%' }}>
      {assessments.map((w, i) => {
        // Resolve unknown statuses to locked once, so rendering and
        // clickability can't disagree (an unknown status must stay inert).
        const status = ASSESSMENT_STATUS[w.status] ? w.status : 'locked';
        const clickable = !!onSelect && status !== 'locked';
        return (
          <div
            key={w.id || i}
            style={{
              flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: size === 'lg' ? 12 : 8, cursor: clickable ? 'pointer' : 'default',
            }}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={clickable ? w.name : undefined}
            onClick={clickable ? () => onSelect(w) : undefined}
            onKeyDown={clickable ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                if (e.key === ' ') e.preventDefault(); // keep Space from scrolling
                onSelect(w);
              }
            } : undefined}
          >
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <HalfLine hidden={i === 0} on={assessments[i - 1] && assessments[i - 1].status === 'complete'} />
              <SeqNode status={status} size={size} />
              <HalfLine hidden={i === assessments.length - 1} on={w.status === 'complete'} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, minWidth: 0, maxWidth: '100%', padding: '0 6px', boxSizing: 'border-box' }}>
              <span style={{
                fontFamily: 'var(--font-sans)', fontWeight: 'var(--w-bold)',
                fontSize: size === 'lg' ? 'var(--text-sm)' : 'var(--text-xs)',
                letterSpacing: 'var(--track-tight)',
                color: status === 'locked' ? 'var(--ink-faint)' : 'var(--ink)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
              }}>{w.name}</span>
              {size === 'lg' && <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--ink-muted)', whiteSpace: 'nowrap' }}>{w.window}</span>}
              {size === 'lg' && (
                <AssessmentStatusPill status={w.status} style={{ marginTop: 3, padding: '3px 9px' }} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
